import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useModal } from '../context/ModalContext';
import { BrightSaaSLogo } from './BrightSaaSLogo';

const MOBILE_MAX = 1199;

export function BrightSaaSMenu() {
  const { open } = useModal();
  const { pathname } = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.innerWidth <= MOBILE_MAX,
  );
  const [fixed, setFixed] = useState(false);
  const menubarRef = useRef<HTMLDivElement>(null);

  // Reveal the gradient background on scroll (replaces the template's
  // one-shot StickyMenu() which toggles `has-fixed`).
  useEffect(() => {
    const onScroll = () => {
      const el = menubarRef.current;
      if (!el) return;
      setFixed(window.scrollY > el.offsetTop);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= MOBILE_MAX);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Lock page scroll while the mobile menu is open (replaces the template's
  // ScrollBar.stop()/start() + .scroll-off behavior).
  useEffect(() => {
    const root = document.documentElement;
    if (menuOpen) {
      root.classList.add('scroll-off');
      return () => root.classList.remove('scroll-off');
    }
  }, [menuOpen]);

  // Close the menu when leaving mobile or navigating.
  useEffect(() => {
    if (!isMobile) setMenuOpen(false);
  }, [isMobile]);
  useEffect(() => {
    setMenuOpen(false);
  }, [pathname]);

  const closeOnMobile = () => {
    if (isMobile) setMenuOpen(false);
  };

  const itemClass = (active: boolean) =>
    active ? 'nk-menu-item active current' : 'nk-menu-item';

  const menubarClass = [
    'nk-menubar js-menubar-fixed was-transparent',
    isMobile ? 'menu-mobile' : '',
    menuOpen ? 'menu-shown' : '',
    fixed ? 'has-fixed' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={menubarClass} id="menu-bar" ref={menubarRef}>
      <div className="container">
        <div className="nk-menubar-warper">
          <div className="nk-menu-background" />
          <BrightSaaSLogo onClick={closeOnMobile} />
          <div className="nk-menu-toggle d-xl-none">
            <button
              type="button"
              aria-label="Toggle menu"
              onClick={() => setMenuOpen((v) => !v)}
              className={
                menuOpen
                  ? 'icon-switch btn btn-icon btn-glow active'
                  : 'icon-switch btn btn-icon btn-glow'
              }>
              <em className="icon icon-inactive ni ni-menu" />
              <em className="icon icon-active ni ni-cross" />
            </button>
          </div>
          <div className="nk-menu">
            <ul className="nk-menu-list">
              <li className={itemClass(pathname === '/')}>
                <Link to="/" className="nk-menu-link" onClick={closeOnMobile}>
                  <span>Home</span>
                </Link>
              </li>
              <li className={itemClass(pathname === '/about')}>
                <Link
                  to="/about"
                  className="nk-menu-link"
                  onClick={closeOnMobile}>
                  <span>About</span>
                </Link>
              </li>
              <li className={itemClass(pathname === '/integrations')}>
                <Link
                  to="/integrations"
                  className="nk-menu-link"
                  onClick={closeOnMobile}>
                  <span>Integrations</span>
                </Link>
              </li>
              <li className={itemClass(pathname === '/pricing')}>
                <Link
                  to="/pricing"
                  className="nk-menu-link"
                  onClick={closeOnMobile}>
                  <span>Pricing</span>
                </Link>
              </li>
              <li className="nk-menu-item">
                <a
                  href="#contact"
                  className="nk-menu-link"
                  onClick={(e) => {
                    e.preventDefault();
                    closeOnMobile();
                    open('contact');
                  }}>
                  <span>Contact</span>
                </a>
              </li>
            </ul>
            <ul className="nk-menu-tools flex-xl-row-reverse column-gap-6">
              <li>
                <button
                  type="button"
                  onClick={() => {
                    closeOnMobile();
                    open('contact');
                  }}
                  className="btn btn-line-animated">
                  <span>Get started</span>
                  <div className="beam-container">
                    <div className="beam-slide">
                      <div className="beam-conic" />
                    </div>
                  </div>
                </button>
              </li>
              <li>
                <button
                  type="button"
                  onClick={() => {
                    closeOnMobile();
                    open('login');
                  }}
                  className="link link-content fw-normal link-hover-title btn btn-link p-0 text-decoration-none">
                  Login
                </button>
              </li>
            </ul>
          </div>
          <div
            id="menu-bar-overlay"
            onClick={() => setMenuOpen(false)}
            className={menuOpen ? 'nk-menu-overlay active' : 'nk-menu-overlay'}
          />
        </div>
      </div>
    </div>
  );
}
